import type { PaymentSource } from "features/payment-methods/types";
import AlertBanners from "features/subscriptions/components/ManageSubscription/AlertBanners";
import { CurrentSubscriptionEstimate } from "features/subscriptions/components/ManageSubscription/CurrentSubscriptionEstimate";
import { EstimateRow } from "features/subscriptions/components/ManageSubscription/EstimateRow";
import NewPrice from "features/subscriptions/components/ManageSubscription/NewPrice";
import ToPayNow from "features/subscriptions/components/ManageSubscription/ToPayNow";
import SubscriptionPlan from "features/subscriptions/components/SubscriptionPlan";
import {
  AGENCY_INCLUDED_SEATS,
  PRICING_PLANS,
} from "features/subscriptions/constants";
import useCreateSubscription from "features/subscriptions/hooks/useCreateSubscription";
import usePricingOptions from "features/subscriptions/hooks/usePricingOptions";
import useCurrentSubscriptions from "features/subscriptions/hooks/useSubscriptions";
import useUpdateOrCreateEstimate from "features/subscriptions/hooks/useUpdateOrCreateEstimate";
import useUpdateSubscription from "features/subscriptions/hooks/useUpdateSubscription";

import { Button } from "common/components/ui/Button";
import { CountSpinner } from "common/components/ui/CountSpinner";
import ErrorMessage from "common/components/ui/ErrorMessage";
import Skeleton from "common/components/ui/Skeleton";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { PricingPeriodInfo, SubscriptionType } from "common/types";

import AgencySeatPricing from "./AgencySeatPricing";
import DesktopLayout from "./DesktopLayout";
import MobileLayout from "./MobileLayout";
import PricingPeriodSelector from "./PricingPeriodSelector";
import UserAndRentalSeatPricing from "./UserAndRentalSeatPricing";

import type { JSX } from "react";

interface ManageSubscriptionDetailsProps {
  subscriptionType: SubscriptionType;
  quantity: number;
  setQuantity: (quantity: number) => void;
  pricingPeriodInfo: PricingPeriodInfo;
  setPricingPeriodInfo: (pricingPeriodInfo: PricingPeriodInfo) => void;
  isPaymentPreviousStep: boolean;
  paymentSourceComponent: JSX.Element;
  paymentSource: PaymentSource;
  goToStep: (step: "success" | "paymentMethod") => void;
}

export default function ManageSubscriptionDetails({
  subscriptionType,
  quantity,
  setQuantity,
  pricingPeriodInfo,
  setPricingPeriodInfo,
  isPaymentPreviousStep,
  paymentSourceComponent,
  paymentSource,
  goToStep,
}: ManageSubscriptionDetailsProps) {
  const isDesktop = useTwBreakpoint("lg");

  const currentSubscriptions = useCurrentSubscriptions();
  const subscriptionDetails = currentSubscriptions[subscriptionType];

  const { pricingOptions } = usePricingOptions(
    subscriptionType,
    subscriptionDetails?.subscription,
  );
  const pricing =
    pricingPeriodInfo &&
    pricingOptions.find(
      ({ plan }) =>
        plan.period === pricingPeriodInfo.period &&
        plan.period_unit === pricingPeriodInfo.period_unit,
    );

  const { price, toPayNowAmount, isLoadingEstimate, estimateError } =
    useUpdateOrCreateEstimate({
      pricing,
      quantity,
      subscriptionId: subscriptionDetails?.subscription.id,
      isCustomerSubscription: subscriptionType === "agency",
    });

  const {
    createSubscription,
    isCreatingSubscription,
    createSubscriptionError,
  } = useCreateSubscription(subscriptionType);

  const {
    updateSubscription,
    isUpdatingSubscription,
    updateSubscriptionError,
  } = useUpdateSubscription(subscriptionType);

  let isDowngrade = false;
  let isUnchanged = false;
  let isCancelled = false;
  let isYearlyToMonthly = false;

  if (subscriptionDetails) {
    isCancelled = subscriptionDetails.subscription.status === "cancelled";
    isDowngrade = quantity < subscriptionDetails.quantity;
    isYearlyToMonthly =
      subscriptionDetails.subscription.item_price.period_unit === "year" &&
      pricingPeriodInfo.period_unit === "month";

    if (pricing) {
      isUnchanged =
        quantity === subscriptionDetails.quantity &&
        pricing.plan.id === subscriptionDetails.subscription.item_price.id;
    }
  }

  const isButtonDisabled =
    (!isCancelled &&
      (isUnchanged ||
        isYearlyToMonthly ||
        (subscriptionType !== "user" && isDowngrade))) ||
    isLoadingEstimate;

  async function onButtonClick() {
    if (!paymentSource) {
      goToStep("paymentMethod");
      return;
    }
    const data = {
      quantity,
      paymentSourceId: paymentSource.id,
      planId: pricing.plan.id,
    };
    if (subscriptionDetails) {
      await updateSubscription({
        ...data,
        accountsToCancel: [],
        paidAmount: toPayNowAmount,
      });
    } else {
      await createSubscription({ ...data, planValue: price });
    }
    goToStep("success");
  }

  let buttonLabel = "Create subscription";

  if (!paymentSource) {
    buttonLabel = "Set payment method";
  } else if (
    !!subscriptionDetails &&
    subscriptionDetails.subscription.status !== "cancelled"
  ) {
    buttonLabel = "Update subscription";
  }

  const subscriptionActionButton = (
    <Button
      size={isDesktop ? "md" : "lg"}
      isLoading={isCreatingSubscription || isUpdatingSubscription}
      onClick={onButtonClick}
      disabled={isButtonDisabled}
    >
      {buttonLabel}
    </Button>
  );

  const currentEstimateComponent = subscriptionDetails &&
    subscriptionDetails.subscription.status !== "cancelled" && (
      <CurrentSubscriptionEstimate
        subscriptionType={subscriptionType}
        quantity={subscriptionDetails.quantity}
        subscriptionId={subscriptionDetails.subscription.id}
        pricingPeriodInfo={subscriptionDetails.subscription.item_price}
      />
    );

  const subscriptionPlanComponent = (
    <SubscriptionPlan plan={subscriptionType} />
  );

  const pricingPeriodSelectionComponent = (
    <PricingPeriodSelector
      pricingOptions={pricingOptions}
      value={pricingPeriodInfo}
      setValue={setPricingPeriodInfo}
    />
  );

  const quantitySelectionComponent = (
    <CountSpinner
      variant={isDesktop ? "sm" : "lg"}
      inputId="seats-spinner"
      value={quantity}
      setValue={setQuantity}
      maxValue={999}
      minValue={subscriptionType === "agency" ? AGENCY_INCLUDED_SEATS : 0}
    />
  );

  const bannersComponent = subscriptionDetails && (
    <AlertBanners
      currentSubscription={subscriptionDetails.subscription}
      quantity={quantity}
      isDowngrade={isDowngrade}
      isYearlyToMonthly={isYearlyToMonthly}
    />
  );

  const newSeatCountComponent = (
    <div className="lg:mt-0.5">
      <EstimateRow
        label={`New no. of ${subscriptionType === "rental" ? "avatars" : "seats"}`}
        tooltipText={PRICING_PLANS[subscriptionType].tooltipText.newQuantity}
        value={quantity}
        valueClassName="text-md-bold"
        isLoading={false}
      />
    </div>
  );

  let pricingComponent = (
    <Skeleton className="h-4 w-16 lg:w-8 max-lg:mx-auto lg:mr-auto" />
  );

  if (pricing) {
    pricingComponent =
      subscriptionType === "agency" ? (
        <AgencySeatPricing pricing={pricing} />
      ) : (
        <UserAndRentalSeatPricing pricing={pricing} />
      );
  }

  const newPriceComponent = (
    <NewPrice
      subscriptionType={subscriptionType}
      pricingPeriodInfo={pricingPeriodInfo}
      price={isUnchanged && !isCancelled ? 0 : price}
      isLoading={isLoadingEstimate}
    />
  );

  const toPayNowComponent = (
    <ToPayNow
      amount={isUnchanged && !isCancelled ? 0 : toPayNowAmount}
      isLoading={isLoadingEstimate}
    />
  );

  const errorMessageComponent = (
    <ErrorMessage
      error={
        estimateError || createSubscriptionError || updateSubscriptionError
      }
      fallbackErrorMessage={
        estimateError ? "Error getting estimate" : "Error mutating subscription"
      }
      size="sm"
    />
  );

  if (isDesktop) {
    const estimateComponents = (
      <>
        <div className="flex flex-col gap-y-2.5">
          {currentEstimateComponent}
          {newSeatCountComponent}
          {newPriceComponent}
        </div>
        {toPayNowComponent}
        {errorMessageComponent}
        {subscriptionActionButton}
        {bannersComponent}
      </>
    );
    return (
      <DesktopLayout
        subscriptionPlanComponent={subscriptionPlanComponent}
        pricingPeriodSelectionComponent={pricingPeriodSelectionComponent}
        quantitySelectionComponent={quantitySelectionComponent}
        pricingComponent={pricingComponent}
        paymentSourceComponent={paymentSourceComponent}
        estimateComponents={estimateComponents}
      />
    );
  }

  const estimateComponents = (
    <>
      {newSeatCountComponent}
      {newPriceComponent}
      {bannersComponent}
      {toPayNowComponent}
    </>
  );

  return (
    <MobileLayout
      subscriptionType={subscriptionType}
      subscriptionPlanComponent={subscriptionPlanComponent}
      pricingPeriodSelectionComponent={pricingPeriodSelectionComponent}
      currentEstimateComponent={currentEstimateComponent}
      quantitySelectionComponent={quantitySelectionComponent}
      pricingComponent={pricingComponent}
      paymentSourceComponent={paymentSourceComponent}
      subscriptionActionButton={subscriptionActionButton}
      estimateComponents={estimateComponents}
      errorMessageComponent={errorMessageComponent}
      isPaymentPreviousStep={isPaymentPreviousStep}
    />
  );
}
